import { createSelector } from 'reselect'

import { combineRolesAndEntities } from './utils'
import { getAuth, getOrgs, getUsers, getSchedules, getTeams } from './base'

export const getUserById = (state, id) => state.users[id] || false

export const getSelfUser = createSelector(
  [getAuth, getUsers],
  (auth, users) => users[auth?.user?.id]
)

/**
 * The raw authenticated-user payload as returned by `/auth/login` and
 * `/auth/refresh`, straight off the auth slice.
 *
 * Distinct from `getSelfUser`, which reads the *normalized* entity — that one
 * is camelCased and drops fields the entity schema does not model (notably
 * `created_at`). Use this when you need a field exactly as the API sent it.
 *
 * Populated by `setCredentials` on every authenticated entry path, so it is
 * available before the app's first screen renders.
 *
 * @type {(state: any) => import('../types/AuthUser').AuthUser | undefined}
 */
export const getAuthUser = createSelector([getAuth], (auth) => auth?.user)

export const getManagedUsers = createSelector(
  [getAuth, getUsers],
  (auth, users) => {
    const managedList = auth.managedUsers
    if (!Array.isArray(managedList)) return false

    return managedList.map((userId) => users[userId] || {})
  }
)

export const makeGetUserRoles = (userByIdSelector) => {
  return createSelector(
    [userByIdSelector, getOrgs, getTeams, getSchedules],
    (user = {}, orgs, teams, schedules) => {
      return combineRolesAndEntities(user.roles, orgs, teams, schedules)
    }
  )
}

/**
 * A reusable method for all entity types, to retrieve the information for their
 * lists of users; if the entity has a `users` or `members` key, then this is
 * how you get that data.
 *
 * @param {function} entityByIdSelector - The selector to retrieve entity info. by id (i.e. getOrgById)
 * @returns {array} - The list of users for the entity
 */
export const makeGetUsers = (entityByIdSelector) => {
  return createSelector([entityByIdSelector, getUsers], (entity, users) => {
    if (!users || !Object.hasOwnProperty.call(entity, 'users')) return []

    const baseUsers = entity.users
    return baseUsers.map((base) => {
      return {
        ...base,
        ...users[base.id],
      }
    })
  })
}
